/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { request } from 'graphql-request'
import { GRAPHQL_ENDPOINT, STRAPI_API_KEY } from './config'
import {
  NAVIGATION_QUERY,
  GLOBAL_QUERY,
  HOMEPAGE_QUERY,
  ABOUT_QUERY,
  CONTACT_QUERY,
  POSTS_QUERY,
  ALL_POSTS_QUERY,
  POSTS_PAGINATED_QUERY,
  NEWS_LIST_QUERY,
  DESIGN_QUERY,
  TECHNOLOGY_QUERY,
  SINGLE_POST_QUERY,
  ADJACENT_POSTS_QUERY,
  PAGE_QUERY,
  SEO_QUERY
} from './queries'

// Helper function to make GraphQL requests
export const fetchFromGraphQL = async (query: string, variables?: any): Promise<any> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (STRAPI_API_KEY) {
      headers['Authorization'] = `Bearer ${STRAPI_API_KEY}`
    }

    const response = await request(GRAPHQL_ENDPOINT, query, variables, headers)
    return response
  } catch (error) {
    //console.error(`Error fetching GraphQL:`, error)
    // Log more details about the error
    if (error instanceof Error && 'response' in error) {
      console.error('GraphQL Error Details:', (error as any).response)
    }
    throw error
  }
}

// API functions for each data type
export const fetchNavigationData = async (): Promise<any> => {
  try {
    const data = await fetchFromGraphQL(NAVIGATION_QUERY)
    return data
  } catch (error) {
    //console.error('Navigation fetch failed:', error)
    throw error
  }
}

export const fetchGlobalData = async (): Promise<any> => {
  const data = await fetchFromGraphQL(GLOBAL_QUERY)
  return data
}

export const fetchHomepageData = async (): Promise<any> => {
  const data = await fetchFromGraphQL(HOMEPAGE_QUERY)
  return data
}

export const fetchAboutData = async (): Promise<any> => {
  return await fetchFromGraphQL(ABOUT_QUERY)
}

export const fetchContactData = async (): Promise<any> => {
  return await fetchFromGraphQL(CONTACT_QUERY)
}

export const fetchPostsData = async (): Promise<any> => {
  return await fetchFromGraphQL(POSTS_QUERY)
}

export const fetchPostsPaginated = async (page: number = 1, pageSize: number = 12): Promise<any> => {
  try {
    // Import sorting utilities dynamically to avoid circular dependencies
    const { sortPostsByEffectiveDate, applyPagination } = await import('../../utils/date')
    
    // Fetch all posts first (without pagination limits)
    //console.log('Fetching all posts for pagination...')
    const data = await fetchFromGraphQL(ALL_POSTS_QUERY)
    const allPosts = data.posts || []
    //console.log(`Total posts fetched: ${allPosts.length}`)
    
    // Sort posts by effective published date
    const sortedPosts = sortPostsByEffectiveDate(allPosts)
    //console.log(`Posts after sorting: ${sortedPosts.length}`)
    
    // Apply pagination manually
    const result = applyPagination(sortedPosts, page, pageSize)
    //console.log(`Page ${page} results: ${result.nodes.length} posts, total pages: ${result.pageInfo.pageCount}`)
    
    return result
  } catch (error) {
    //console.error('Error fetching paginated posts:', error)
    throw error
  }
}

export const fetchNewsListData = async (): Promise<any> => {
  return await fetchFromGraphQL(NEWS_LIST_QUERY)
}

export const fetchDesignData = async (): Promise<any> => {
  return await fetchFromGraphQL(DESIGN_QUERY)
}

export const fetchTechnologyData = async (): Promise<any> => {
  return await fetchFromGraphQL(TECHNOLOGY_QUERY)
}

export const fetchSinglePost = async (slug: string): Promise<any> => {
  try {
    const data = await fetchFromGraphQL(SINGLE_POST_QUERY, { slug })
    return data.posts?.[0] || null
  } catch (error) {
    //console.error('Error fetching single post:', error)
    return null
  }
}

export const fetchAdjacentPosts = async (currentSlug: string): Promise<any> => {
  try {
    // Import sorting utilities
    const { sortPostsByEffectiveDate, getEffectivePublishedDate } = await import('../../utils/date')
    
    // Get all posts and sort them by effective date
    const allPostsData = await fetchFromGraphQL(ALL_POSTS_QUERY)
    const allPosts = allPostsData.posts || []
    
    // Sort posts by effective published date
    const sortedPosts = sortPostsByEffectiveDate(allPosts)
    
    // Find the current post index in sorted array
    const currentIndex = sortedPosts.findIndex(post => post.slug === currentSlug)
    
    if (currentIndex === -1) {
      return { previousPost: null, nextPost: null }
    }
    
    // Previous post is the one before current in sorted order (older)
    const previousPost = currentIndex < sortedPosts.length - 1 ? sortedPosts[currentIndex + 1] : null
    
    // Next post is the one after current in sorted order (newer) 
    const nextPost = currentIndex > 0 ? sortedPosts[currentIndex - 1] : null
    
    return {
      previousPost,
      nextPost
    }
  } catch (error) {
    console.error('Error fetching adjacent posts:', error)
    return { previousPost: null, nextPost: null }
  }
}

export const fetchPageData = async (slug: string): Promise<any> => {
  try {
    //console.log(`API: Fetching page with slug "${slug}"`)
    //console.log('GraphQL Query:', PAGE_QUERY)
    
    const data = await fetchFromGraphQL(PAGE_QUERY, { slug })
    
    //console.log('📡 GraphQL Response:', JSON.stringify(data, null, 2))
    
    const page = data.pages?.[0] || null
    
    if (page) {
      //console.log(`API: Page found - Title: "${page.title}", Published: ${page.publishedAt}`)
    } else {
      //console.log('API: No page found in response')
      if (data.pages) {
        //console.log(`Total pages in response: ${data.pages.length}`)
      }
    }
    
    return page
  } catch (error) {
    //console.error('API Error fetching page:', error)
    return null
  }
}

export const fetchSeoData = async (): Promise<any> => {
  try {
    const data = await fetchFromGraphQL(SEO_QUERY)
    return data.seo || null
  } catch (error) {
    //console.error('Error fetching SEO data:', error)
    return null
  }
} 